import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useTestimonials, useApproveTestimonial, type Testimonial } from "@/hooks/useTestimonials";
import { toast } from "sonner";

type Status = "pending" | "waiting" | "approved";

const STATUS: Record<Status, { label: string; cls: string }> = {
  pending: { label: "Aguardando aprovação", cls: "bg-warn-soft text-warn" },
  waiting: { label: "Aguardando cliente", cls: "bg-muted text-muted-foreground" },
  approved: { label: "Publicado", cls: "bg-ok-soft text-ok" },
};

function statusOf(t: Testimonial): Status {
  if (t.approved) return "approved";
  if (t.submitted_at) return "pending";
  return "waiting";
}

interface TestimonialsManagerProps {
  /** Rendered inside ContentHub — hides the standalone header. */
  embedded?: boolean;
}

export function TestimonialsManager({ embedded = false }: TestimonialsManagerProps) {
  const { data: items = [], isLoading } = useTestimonials();
  const approve = useApproveTestimonial();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const order: Status[] = ["pending", "waiting", "approved"];
  const sorted = [...items].sort((a, b) => order.indexOf(statusOf(a)) - order.indexOf(statusOf(b)));

  return (
    <div className="flex flex-col gap-[22px]">
      {!embedded && (
        <header>
          <h1 className="mb-1.5 font-display text-[27px] font-bold tracking-[-0.03em]">
            Depoimentos
          </h1>
          <p className="text-[13.5px] text-muted-foreground">
            Aprove depoimentos antes de publicar nos cases
          </p>
        </header>
      )}

      {sorted.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground shadow-card">
          Nenhum depoimento ainda. Solicite um na etapa de pagamentos do lead.
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
        {sorted.map((t) => {
          const st = statusOf(t);
          const stars = t.rating
            ? "★".repeat(t.rating) + "☆".repeat(Math.max(0, 5 - t.rating))
            : null;
          return (
            <div
              key={t.id}
              className="flex flex-col gap-3 rounded-2xl border border-border bg-card px-[22px] py-5 shadow-card"
            >
              <div className="flex items-start justify-between gap-2.5">
                <span className="text-sm tracking-[0.1em] text-warn">{stars ?? "—"}</span>
                <span
                  className={`inline-flex h-[22px] items-center whitespace-nowrap rounded-[7px] px-[9px] text-[10.5px] font-bold ${STATUS[st].cls}`}
                >
                  {STATUS[st].label}
                </span>
              </div>
              <p className="text-[13px] leading-[1.6] text-muted-foreground">
                {t.quote ? (
                  `"${t.quote}"`
                ) : (
                  <span className="italic">Cliente ainda não enviou o depoimento.</span>
                )}
              </p>
              <div className="mt-auto flex items-end justify-between gap-3 border-t border-border pt-3">
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-bold">{t.author_name}</div>
                  <div className="truncate text-[11.5px] text-muted-foreground">
                    {t.author_role || "—"}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {st === "pending" && (
                    <>
                      <Button
                        variant="outline"
                        className="h-[30px] rounded-lg px-3 text-xs font-semibold"
                        onClick={() => approve.mutate({ id: t.id, approved: false })}
                      >
                        Recusar
                      </Button>
                      <Button
                        className="h-[30px] rounded-lg px-3 text-xs font-semibold"
                        onClick={() => approve.mutate({ id: t.id, approved: true })}
                      >
                        Aprovar
                      </Button>
                    </>
                  )}
                  {st === "waiting" && t.public_token && (
                    <Button
                      variant="outline"
                      className="h-[30px] rounded-lg px-3 text-xs font-semibold"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/feedback/${t.public_token}`,
                        );
                        toast.success("Link copiado");
                      }}
                    >
                      Copiar link
                    </Button>
                  )}
                  {st === "approved" && (
                    <Button
                      variant="outline"
                      className="h-[30px] rounded-lg px-3 text-xs font-semibold"
                      onClick={() => approve.mutate({ id: t.id, approved: false })}
                    >
                      Despublicar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
